import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useMemo } from 'react';
import { Users } from '@bridebook/models';
import { PremiumTiers } from '@bridebook/models/source/models/Suppliers/Admin/admin.types';
import { buildCompactWeddingFeatures } from '@bridebook/toolbox/src/recommendations/wedding-features';
import { SupplierHistory } from '@bridebook/web/lib/api/recommendations/utils';
import { getVenueMatchesRange, mapSlugToFriendlyUrlSlug } from '@bridebook/toolbox';
import { getCurrentUser } from '@bridebook/toolbox/src/api/auth/getCurrentUser';
import gazetteer, { Market } from '@bridebook/toolbox/src/gazetteer';
import { getWeddingDateRange } from '@bridebook/toolbox/src/search-suppliers/get-wedding-date-range';
import { toMillis } from '@bridebook/toolbox/src/time-utils/time-utils';
import { UrlFriendlySlug } from '@bridebook/toolbox/src/types';
import { useQuery } from '@tanstack/react-query';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { ISearchPageQuery, SortOptionValue } from '@bridebook/web/app-shared/lib/search/types';
import { createFullQuery } from '@bridebook/web/app-shared/lib/search/utils';
import { isValidForMarket } from '@bridebook/web/app-shared/lib/supplier/utils/is-valid-for-market';
import { fetchSearchPromise } from '@bridebook/web/lib/search/actions';
import { SearchQueryKey } from '@bridebook/web/lib/search/constants';
import { useLocationData } from '@bridebook/web/lib/search/hooks/query/use-location-data';
import { useWeddingVenueInteractions } from '@bridebook/web/lib/search/hooks/query/use-wedding-venue-interactions';
import { IFetchSearchPromiseArgs } from '@bridebook/web/lib/search/types';
import { getCountryCodeFromArea } from '@bridebook/web/lib/search/utils/get-country-code-from-area';
import { useIsAppendIncompleteSearchEnabled } from '@bridebook/web/lib/ab-tests/tests/global/LIVE-27907_AppendIncompleteSearchResults';

export const SEARCH_QUERY_CACHE_TIME = toMillis(15, 'min');

export type ISearchParams = Omit<ISearchPageQuery, 'slug' | 'searchParams'> & {
  slug: UrlFriendlySlug;
  page?: number;
  facets: string[];
  searchParams: string[];
  sort?: SortOptionValue;
  market: Market;
  placeId?: string;
  isCurated?: boolean;
  weddingMonths?: number[];
  weddingWeekDays?: string[];
  hasLateAvailability?: boolean;
  minTier?: number;
};

export const OMIT_ENQ_MODAL_PARAMETERS = [
  'enquiryContext',
  'enquirySupplierId',
  'multiEnquiry',
  'enquiryConfirmation',
  'enquirySupplier',
];

export const useSearchParams = (): ISearchParams | undefined => {
  const router = useRouter();
  const { query, isReady } = router;
  const weddingMarket = useMarket();
  return useMemo(() => {
    if (!isReady) return undefined;
    const { page, sort } = query as ParsedUrlQuery & { sort: SortOptionValue };
    const searchParams = (query.searchParams ?? []) as string[];
    // searchParams[0] is supplier category slug
    const storageSlug = searchParams[0]?.substring('wedding-'.length) as UrlFriendlySlug;

    // Check if the slug passed from local storage is valid, otherwise fallback to default
    // in rare cases, a search param slug can store anomalies such as wedding-undefined which bricks the search
    const slug =
      storageSlug && mapSlugToFriendlyUrlSlug(storageSlug).isValidSlug ? storageSlug : 'venues';

    // searchParams[1] is area
    const area = searchParams[1] || 'uk';
    const facets = searchParams.slice(2) as string[];
    const countryCodeWithFallback = getCountryCodeFromArea(area);
    const market = gazetteer.getMarketByCountry(countryCodeWithFallback);
    /**
     * We need to override the locale of the market to the locale of the wedding market in order to
     * be able to use market prefix. For example the default prefix for FR market is `fr`, but with
     * locale set to `en` the prefix will be `fr-en`.
     */
    market.locale = weddingMarket.locale;
    const isGlobalSearch = market.country !== weddingMarket.country;
    const forceIsCuratedParam =
      weddingMarket.hasSetting('forcedCuratedListSearch') && !isGlobalSearch;

    return {
      ...query,
      // clean up parameters of enq modal
      id: undefined,
      enquiryContext: undefined,
      enquirySupplierId: undefined,
      multiEnquiry: undefined,
      enquiryConfirmation: undefined,
      enquirySupplier: undefined,
      // end clean up parameters of enq modal
      slug,
      area,
      facets: facets ?? [],
      sort,
      page: page ? Number(page) : 1,
      searchParams,
      market,
      ...(forceIsCuratedParam
        ? {
            isCurated:
              router.query.isCurated === 'true' || typeof router.query.isCurated === 'undefined',
          }
        : {}),
    };
  }, [isReady, query, router.query.isCurated, weddingMarket.country, weddingMarket.locale]);
};

/**
 * Enriches search parameters with wedding-specific data.
 * Adds contextual information such as venue interactions for ML reranking, wedding date ranges,
 * budget constraints, and venue capacity requirements based on the search type and wedding details.
 *
 * `venueHistory` is supplied by callers from `useWeddingVenueInteractions` so the
 * expensive Firestore query that materialises it only runs once per session
 * (cached for 15 minutes) instead of on every search refetch.
 */
export const getDerivedParams = async (
  searchParams: ISearchParams,
  venueHistory?: SupplierHistory,
) => {
  const currentUser = await getCurrentUser();
  if (!currentUser) {
    return {};
  }

  const wedding = await Users._.getById(currentUser.uid).getActiveWedding();
  if (!wedding) {
    return {};
  }

  const derivedParams: Record<string, any> = {};

  // Venue interactions for ML reranking (pre-fetched via useWeddingVenueInteractions)
  if (searchParams.slug === 'venues' && venueHistory && venueHistory.user_history.length > 0) {
    derivedParams['interactions'] = venueHistory.user_history;
    derivedParams['interactionActions'] = venueHistory.action_history;
    derivedParams['weddingId'] = wedding.id;
    const compactFeatures = buildCompactWeddingFeatures(wedding);
    if (Object.keys(compactFeatures).length > 0) {
      derivedParams['weddingFeatures'] = compactFeatures;
    }
  }

  // Query string has only hasLateAvailability=true, but search engine has to receive couple's wedding date
  const hasLateAvailability = searchParams?.searchParams?.includes('hasLateAvailability');
  if (hasLateAvailability && wedding?.date) {
    derivedParams['weddingDateRange'] = getWeddingDateRange(wedding.date, wedding.l10n?.country);
  }

  // If params have weddingMonths or weddingWeekDays search engine has to receive couple's wedding budget.
  const needsBudgetFilter = searchParams.weddingMonths || searchParams.weddingWeekDays;
  if (needsBudgetFilter) {
    derivedParams['weddingBudget'] = wedding?.budget ?? searchParams?.market?.config?.averageCost;
  }

  // If user doesn't have location set yet, and has guests estimates, then return filters with capacityDining filter
  const estimate = wedding.guests?.estimate;
  const shouldAddCapacity = !wedding.location && estimate && searchParams.slug === 'venues';
  if (shouldAddCapacity) {
    const capacityDining = getVenueMatchesRange(estimate);
    if (capacityDining !== '0-0') {
      derivedParams.capacityDining = capacityDining;
    }
  }

  return derivedParams;
};

export const useSearch = (params?: ISearchParams) => {
  const LIVE_27907_appendIncompleteSearch = useIsAppendIncompleteSearchEnabled(
    params?.slug,
    params?.market?.country,
  );
  const { locationData, isLocationDataFetched } = useLocationData({
    slug: params?.slug,
    area: params?.area,
    market: params?.market,
  });
  const { data: venueHistory, isFetched: isVenueHistoryFetched } = useWeddingVenueInteractions();
  const { data: searchData, isLoading: isSearchDataLoading } = useQuery(
    [SearchQueryKey.SearchData, params],
    async ({ signal }) => {
      if (params === undefined) return;
      const derivedParams = await getDerivedParams(params, venueHistory);
      const fullQuery = createFullQuery(params);
      const request: IFetchSearchPromiseArgs = {
        ...fullQuery,
        ...derivedParams,
        page: params.page,
        isLoggedIn: true,
        locationData,
        countryCode: params.market.country,
        locale: params.market.locale,
        ...(LIVE_27907_appendIncompleteSearch
          ? { minTier: PremiumTiers.Tier_0_25, LIVE_27907_AppendIncompleteSearch: true }
          : {}),
      };

      const result = {
        fullQuery,
        params,
        request,
        response: await fetchSearchPromise(request, signal),
        locationData: locationData ?? null,
      };

      const validForMarket = isValidForMarket({
        supplierCountry: result.response?.fields?.addressComponents?.country,
        market: params.market,
        isLoggedIn: true,
      });

      if (!validForMarket) {
        throw new Error();
      }

      return result;
    },
    {
      cacheTime: SEARCH_QUERY_CACHE_TIME,
      staleTime: SEARCH_QUERY_CACHE_TIME,
      enabled: isLocationDataFetched && !!params && isVenueHistoryFetched,
    },
  );

  return {
    data: params ? searchData : undefined,
    isLoading: isSearchDataLoading || !isLocationDataFetched || !isVenueHistoryFetched,
  };
};
